import { useEffect } from "react";
import { Box, Button, Group, Select, Text, TextInput } from "@mantine/core";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { Controller, useForm } from "react-hook-form";
import { DateInput } from "@mantine/dates";
import {
  clearSingleClientReports,
  setFiltersSingleClientReport,
  setPageSingleClientReport,
  setSizeSingleClientReport,
  useSelectSelected,
  useSelectSelectionMode,
  useSelectTotalCountSingleClientReport,
  unSelectAll,
} from "../../../../redux/features/reports/singleClientReportSlice";
import useSingleClientReportFilters from "./useSingleClientReportFilters.logic";
import ExportSingleReports from "../../exportButtons/ExportSingleReports";
import { REPORT_TYPES } from "../../constants";
import { useQueryParams } from "../../../../hooks/useQueryParams";

function SelectedCountLabel() {
  const selected = useSelectSelected();
  const selectionMode = useSelectSelectionMode();
  const totalCount = useSelectTotalCountSingleClientReport();

  const count = selectionMode === "all" ? totalCount : selected.length;

  if (count === 0) return null;

  const label =
    selectionMode === "all"
      ? `All ${count} records selected`
      : `${count} record${count !== 1 ? "s" : ""} selected`;

  return (
    <Text size="sm" c="secondary.9" fw={500}>
      {label}
    </Text>
  );
}

const SingleClientReportFilters = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { isLoading, enabled, setEnabled } = useSingleClientReportFilters();

  const { params, setSearchParams, adjustFilter } = useQueryParams();

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { isDirty },
  } = useForm({
    defaultValues: {
      periodFrom: "",
      periodTo: "",
      clientType: "",
      clientUic: "",
      cid: "",
      name: "",
    },
  });

  useEffect(() => {
    const haveParams = Object.keys(params).length !== 0;

    for (const element in params) {
      setValue(element, params[element], { shouldDirty: true });
    }

    const { page: pageFromQuery, size: sizeFromQuery } = params;
    if (pageFromQuery) {
      dispatch(setPageSingleClientReport(pageFromQuery));
    }
    if (sizeFromQuery) {
      dispatch(setSizeSingleClientReport(sizeFromQuery));
    }

    if (haveParams && !enabled) {
      dispatch(setFiltersSingleClientReport(params));
      setEnabled(true);
      return;
    }

    if ((pageFromQuery || sizeFromQuery) && !enabled) {
      setEnabled(true);
    }

    return () => {
      onClear();
      dispatch(setEnabled(false));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function onSubmit(draftFilters) {
    dispatch(setFiltersSingleClientReport(draftFilters));
    dispatch(setPageSingleClientReport(0));
    dispatch(setSizeSingleClientReport(20));
    dispatch(unSelectAll());
    setSearchParams(draftFilters);
    adjustFilter("size", 20);
    adjustFilter("page", 0);
    setEnabled(true); // on first render request will be heavy, so we skip initial load.
  }

  function onClear() {
    reset();
    setSearchParams({});
    dispatch(clearSingleClientReports());
  }

  return (
    <Box bg="gray.1" p="md">
      <Group justify="space-between">
        <Group>
          <ExportSingleReports type={REPORT_TYPES.SINGLE} />
          <SelectedCountLabel />
        </Group>
        <Group>
          <Button
            disabled={!isDirty}
            variant="outline"
            color="black"
            onClick={onClear}
          >
            Clear
          </Button>
          <Button
            disabled={isLoading || !isDirty}
            onClick={handleSubmit(onSubmit)}
          >
            Generate report
          </Button>
        </Group>
      </Group>

      <form onSubmit={handleSubmit(onSubmit)}>
        <Group mt="md">
          <Controller
            name="periodFrom"
            control={control}
            render={({ field }) => (
              <DateInput
                {...field}
                w={270}
                label={t("reports.fromDate")}
                placeholder="yyyy-MM-dd"
                valueFormat="YYYY-MM-DD"
                clearable
              />
            )}
          />
          <Controller
            name="periodTo"
            control={control}
            render={({ field }) => (
              <DateInput
                {...field}
                w={270}
                label={t("reports.toDate")}
                placeholder="yyyy-MM-dd"
                valueFormat="YYYY-MM-DD"
                clearable
              />
            )}
          />
          <Controller
            name="clientType"
            control={control}
            render={({ field }) => (
              <Select
                key={new Date().getTime()} // force clear
                {...field}
                w={270}
                label={t("reports.clientType")}
                clearable
                data={[
                  { value: "natural", label: t("reports.naturalPerson") },
                  { value: "legal", label: t("reports.legalEntity") },
                ]}
              />
            )}
          />
          <Controller
            name="clientUic"
            control={control}
            render={({ field }) => (
              <TextInput {...field} w={270} label={t("reports.clientNumber")} />
            )}
          />
          <Controller
            name="cid"
            control={control}
            render={({ field }) => (
              <TextInput {...field} w={270} label={t("reports.offerId")} />
            )}
          />
          <Controller
            name="name"
            control={control}
            render={({ field }) => (
              <TextInput {...field} w={270} label={t("reports.clientName")} />
            )}
          />
        </Group>
      </form>
    </Box>
  );
};

export default SingleClientReportFilters;
